import type { AnalysisResultDetailedSchema } from "@/app/shared/services/volateq-api/api-schemas/analysis-result-schema";

export interface EvalBatchPill {
  num: number;
  color: string;
  released: boolean;
}

export class EvalBatchPillsHelper {
  /**
   * EB pills of an analysis result, sorted by eval batch number.
   *
   * An AnalysisResultEvalBatch only exists once the results of that EB have been imported.
   * Customers only get to see the released ones. VolaTeQ admins additionally see the already
   * imported but not yet released ones, flagged as unreleased so they can be rendered
   * differently (the API omits them for customers anyway).
   */
  static getEvalBatchPills(
    analysisResult: AnalysisResultDetailedSchema | undefined,
    isAdmin: boolean,
  ): EvalBatchPill[] {
    return (analysisResult?.eval_batches || [])
      .filter(arEvalBatch => isAdmin || arEvalBatch.released)
      .map(arEvalBatch => ({
        num: arEvalBatch.eval_batch.num,
        color: arEvalBatch.eval_batch.color,
        released: arEvalBatch.released,
      }))
      .sort((a, b) => a.num - b.num);
  }
}

export class EvalBatchSelectionHelper {
  /**
   * The EB selection after (un)selecting "num". null stands for all EBs (= no restriction): it is
   * the start, and unselecting the last EB or picking every EB returns to it. That also keeps
   * the results that carry no EB at all.
   */
  static toggle(selectedNums: number[] | null, num: number, allNums: number[]): number[] | null {
    const currentNums = selectedNums ?? [];
    const nextNums = currentNums.includes(num)
      ? currentNums.filter(currentNum => currentNum !== num)
      : [...currentNums, num].sort((a, b) => a - b);

    return nextNums.length === 0 || nextNums.length === allNums.length ? null : nextNums;
  }

  /** The "eval_batch_nums" API param, omitted for all EBs. */
  static toQueryParam(selectedNums: number[] | null): { eval_batch_nums?: string } {
    return selectedNums ? { eval_batch_nums: selectedNums.join(",") } : {};
  }
}
